/**
 * "← Modify search" pill (Iter 67c).
 *
 * Surfaces the renter's hero-stepper selections (country, city, dates) on the
 * car-detail page as a single clickable chip. Clicking it goes back to the
 * homepage stepper with the same querystring so the user can tweak any field
 * without losing the rest of their picks.
 *
 * Renders nothing when no relevant params are present — keeps the car-detail
 * page clean for users who landed via a direct/saved link.
 */
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarDays } from "lucide-react";
import { formatLocalDay } from "@/lib/localDate";

export default function ModifySearchPill() {
  const [params] = useSearchParams();
  const country = params.get("country");
  const pickup = params.get("pickup_location");
  const dropoff = params.get("dropoff_location");
  const start = params.get("start_date");
  const end = params.get("end_date");

  // Only show the pill when the renter actually came from a search — otherwise
  // we'd be promoting a phantom "search" they never did.
  if (!country && !pickup && !start && !end) return null;

  const where = [pickup, dropoff && dropoff !== pickup ? `→ ${dropoff}` : null]
    .filter(Boolean)
    .join(" ");
  const when = start && end
    ? `${formatLocalDay(start, { fallback: "" })} — ${formatLocalDay(end, { fallback: "" })}`
    : null;

  const href = `/?${params.toString()}`;

  return (
    <Link
      to={href}
      className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-gradient-to-r from-sky-50 to-indigo-50 border border-indigo-100 text-xs font-medium text-indigo-700 hover:from-sky-100 hover:to-indigo-100 hover:border-indigo-200 transition group"
      data-testid="modify-search-pill"
    >
      <ArrowLeft className="h-3.5 w-3.5 text-indigo-500 group-hover:-translate-x-0.5 transition-transform" />
      <span className="text-neutral-500 uppercase tracking-wider text-[10px] font-semibold">Modify search</span>
      {country && (
        <span className="px-2 py-0.5 rounded-full bg-white/70 border border-indigo-100 text-indigo-700">
          {country}
        </span>
      )}
      {where && (
        <span className="inline-flex items-center gap-1 text-neutral-700">
          <MapPin className="h-3 w-3 text-violet-500" />
          {where}
        </span>
      )}
      {when && (
        <span className="inline-flex items-center gap-1 text-neutral-700">
          <CalendarDays className="h-3 w-3 text-amber-500" />
          {when}
        </span>
      )}
    </Link>
  );
}
